import type { QueryClient } from "@tanstack/react-query";
import {
  type PersistedClient,
  type Persister,
  persistQueryClient,
} from "@tanstack/react-query-persist-client";
import { del, delMany, get, keys, set } from "idb-keyval";

// One entry per login per company, so a switch never hands a tab the other company's rows.
const HELD = "foreday";

export const KEEP_FOR = 24 * 60 * 60_000;

/** Set at build time; a cache written by another build is discarded rather than hydrated. */
const BUILD = import.meta.env.VITE_BUILD ?? "dev";

// IndexedDB, not `localStorage`: a synchronous write of the working set would block the main thread.
export function persister(userId: string, companyId: string): Persister {
  const held = keyFor(userId, companyId);
  return {
    persistClient: (client: PersistedClient) => set(held, client),
    restoreClient: () => get<PersistedClient>(held),
    removeClient: () => del(held),
  };
}

const keyFor = (userId: string, companyId: string) =>
  `${HELD}:${userId}:${companyId}`;

/** The library saves the whole working set on every cache event; one write a second is enough. */
const WRITE_EVERY = 1_000;

function throttled(inner: Persister): {
  persister: Persister;
  cancel: () => void;
} {
  let latest: PersistedClient | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  return {
    persister: {
      ...inner,
      persistClient: (client) => {
        latest = client;
        timer ??= setTimeout(() => {
          timer = undefined;
          const next = latest;
          latest = null;
          if (next) void inner.persistClient(next);
        }, WRITE_EVERY);
      },
    },
    // A write still waiting when the pair is left would put back what signing out just emptied.
    cancel: () => {
      clearTimeout(timer);
      timer = undefined;
      latest = null;
    },
  };
}

let saving: { held: string; stop: () => void } | null = null;

// Called from the company guard, not a component, so the cache is restored before a loader reads it.
export async function keepAcrossReloads(
  queryClient: QueryClient,
  userId: string,
  companyId: string,
): Promise<void> {
  // A private or locked-down window has no IndexedDB and reads afresh.
  if (typeof indexedDB === "undefined") return;
  const held = keyFor(userId, companyId);
  if (saving?.held === held) return;
  saving?.stop();
  const writes = throttled(persister(userId, companyId));
  const [unsubscribe, restoring] = persistQueryClient({
    queryClient,
    persister: writes.persister,
    maxAge: KEEP_FOR,
    buster: BUILD,
  });
  saving = {
    held,
    stop: () => {
      unsubscribe();
      writes.cancel();
    },
  };
  await restoring;
}

// Every key in the store, not only the current company's: another tab may have saved a second, and
// a cache written under an earlier key name holds the same rows. Nothing else uses this store.
export async function forgetPersisted(): Promise<void> {
  saving?.stop();
  saving = null;
  const kept = await keys();
  if (kept.length > 0) await delMany(kept);
}
